/**
 * Отправка данных объекта в NetBox — через согласование.
 *
 * Окно показывает, что именно изменится в NetBox: серийные номера,
 * стойки с размещением, заведение отсутствующих устройств. Отправить
 * отсюда напрямую нельзя:
 *   • редактор отмечает объект «Готово к отправке»;
 *   • координатор выбирает части и создаёт запрос с комментарием;
 *   • контролёр данных проверяет запрос в разделе «Запросы в NetBox»
 *     и одобряет его (данные уходят в NetBox) или отклоняет.
 */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { api } from '../../api/client';
import { selectUser } from '../../store/slices/authSlice';
import { refreshNetbox } from '../../store/slices/netboxSlice';
import {
  blocksFromSelection, hasAnyBlock, canCreateRequest, canMarkReady, canSeeRequests, describeCounts, statusLabel, formatDateTime,
} from '../../domain/netboxRequests';
import styles from './NetboxExport.module.css';

const TABS = [
  { id: 'serials', label: 'Серийные номера' },
  { id: 'racks', label: 'Стойки и размещение' },
  { id: 'devices', label: 'Новые устройства' },
];

export default function NetboxExport({ mag, objectLabel, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [tab, setTab] = useState('serials');
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  // Текст выполняемого действия; пусто — ничего не выполняется
  const [sending, setSending] = useState('');
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);
  // Что координатор включает в запрос. Переносы между стойками —
  // существенное изменение, поэтому выключены, пока их не отметят.
  const [selection, setSelection] = useState({ serials: true, racks: true, devices: true, moves: false });
  const [comment, setComment] = useState('');
  const [openRequest, setOpenRequest] = useState(null);
  const [ready, setReady] = useState(null);

  const isCoordinator = canCreateRequest(user);
  const isEditor = canMarkReady(user);
  // Подробности запроса — номер, автор, состав — только участникам
  // согласования. Редактор видит лишь, что объект на проверке.
  const seesRequests = canSeeRequests(user);
  const includeMoves = selection.moves;
  const setIncludeMoves = (v) => setSelection((s) => ({ ...s, moves: v }));
  const toggle = (key) => (e) => setSelection((s) => ({ ...s, [key]: e.target.checked }));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = encodeURIComponent(mag);
      const [preview, open, readyList] = await Promise.all([
        api.get(`/api/netbox/export/preview?mag=${q}`),
        seesRequests ? api.get(`/api/netbox/requests?mag=${q}&status=open`) : Promise.resolve(null),
        api.get('/api/netbox/ready'),
      ]);
      setPlan(preview);
      setOpenRequest(open?.requests[0] || null);
      setReady(readyList.ready.find((r) => r.mag === mag) || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [mag, seesRequests]);

  useEffect(() => { load(); }, [load]);

  // Объект на проверке: у участников согласования — по самому запросу,
  // у редактора — по признаку из плана.
  const underReview = Boolean(openRequest || plan?.underReview);

  useEffect(() => {
    function handleKey(e) { if (e.key === 'Escape' && !sending) onClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, sending]);

  const blocks = blocksFromSelection(selection, plan);

  async function createRequest() {
    setSending('Отправляю на проверку…');
    setError(null);
    try {
      const request = await api.post('/api/netbox/requests', { mag, blocks, comment: comment.trim() });
      setCreated(request);
      setComment('');
      dispatch(refreshNetbox());
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSending('');
    }
  }

  // Отметка не меняет план — перечитывается только она сама
  async function toggleReady() {
    setSending('Сохраняю…');
    setError(null);
    try {
      if (ready) await api.delete(`/api/netbox/ready/${encodeURIComponent(mag)}`);
      else await api.post('/api/netbox/ready', { mag });
      const list = await api.get('/api/netbox/ready');
      setReady(list.ready.find((r) => r.mag === mag) || null);
      dispatch(refreshNetbox());
    } catch (e) {
      setError(e.message);
    } finally {
      setSending('');
    }
  }

  const goToRequest = (id) => { onClose(); navigate(`/netbox/${id}`); };

  /** Отметка «включить в запрос» над содержимым вкладки — для координатора. */
  const includeBox = (key, count, disabledReason) => isCoordinator && !openRequest && count > 0 && (
    <label className={`${styles.checkbox} ${styles.include}`} title={disabledReason || ''}>
      <input type="checkbox" checked={Boolean(selection[key]) && !disabledReason} disabled={Boolean(disabledReason)} onChange={toggle(key)} />
      Включить в запрос
      {disabledReason && <span className={styles.muted}> — {disabledReason}</span>}
    </label>
  );

  const serials = plan?.serials;
  const racks = plan?.racks;
  const devices = plan?.devices;
  const overwrites = serials?.toSend.filter((x) => x.overwrites).length ?? 0;
  const newRacks = racks?.racks.filter((r) => !r.existingId).length ?? 0;

  return (
    <>
      <button className={styles.overlay} onClick={() => !sending && onClose()} aria-label="Закрыть" />

      <div className={styles.modal} role="dialog" aria-label="Отправка в NetBox">
        <div className={styles.header}>
          <div>
            <div className={styles.title}>Отправка в NetBox</div>
            <div className={styles.subtitle}>{objectLabel}</div>
          </div>
          <div className={styles.spacer} />
          <button className={styles.close} onClick={onClose} disabled={Boolean(sending)} aria-label="Закрыть">×</button>
        </div>

        <div className={styles.tabs}>
          {TABS.map((t) => (
            <button
              key={t.id}
              className={`${styles.tab} ${tab === t.id ? styles.tabActive : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {plan && <span className={styles.badge}>{countFor(t.id, plan)}</span>}
            </button>
          ))}
        </div>

        <div className={styles.body}>
          {loading && <div className={styles.intro}>Сверяю объект с NetBox…</div>}
          {error && <div className={styles.error}>{error}</div>}

          {plan && !plan.writeEnabled && (
            <div className={styles.warning}>
              Токен записи NetBox не задан на сервере — изменения можно только просмотреть.
            </div>
          )}

          {plan && plan.siteProblem && tab !== 'serials' && (
            <div className={styles.warning}>{plan.siteProblem}</div>
          )}

          {openRequest && (
            <div className={styles.note}>
              По объекту есть запрос <b>№{openRequest.id}</b> — {statusLabel(openRequest.status).toLowerCase()}:
              {' '}{describeCounts(openRequest.counts)}. Создал {openRequest.createdByName || openRequest.createdBy}
              {' '}{formatDateTime(openRequest.createdAt)}.
              {' '}Правки данных объекта сделают его устаревшим.
              {seesRequests && <>{' '}<button className={styles.link} onClick={() => goToRequest(openRequest.id)}>Открыть запрос</button></>}
            </div>
          )}

          {!openRequest && underReview && !loading && (
            <div className={styles.note}>
              Данные объекта сейчас на проверке перед отправкой в NetBox. Правки сделают
              проверку недействительной — если они нужны, сообщите координатору.
            </div>
          )}

          {created && (
            <div className={styles.ok}>
              Запрос <b>№{created.id}</b> отправлен на проверку контролёру данных: {describeCounts(created.counts)}.
              {seesRequests && <>{' '}<button className={styles.link} onClick={() => goToRequest(created.id)}>Открыть запрос</button></>}
            </div>
          )}

          {!isCoordinator && !underReview && !created && plan && !loading && (
            <div className={styles.note}>
              Отправку в NetBox запрашивает координатор, одобряет контролёр данных.
              {isEditor && ' Заполнив данные объекта, отметьте его «Готово к отправке» — координатор увидит его в своём списке.'}
              {ready && <> Объект отмечен готовым: {ready.markedByName || ready.markedBy}, {formatDateTime(ready.markedAt)}.</>}
            </div>
          )}

          {/* ── Серийные номера ── */}
          {plan && !loading && tab === 'serials' && (
            <>
              <p className={styles.intro}>
                Источник истины — Лира. Значение из поля SN Lyra записывается в NetBox
                и перекрывает имеющееся.
              </p>
              {includeBox('serials', serials.toSend.length)}
              {serials.toSend.length === 0 ? (
                <div className={styles.ok}>Расхождений нет — отправлять нечего.</div>
              ) : (
                <Table head={['Коммутатор', 'Было в NetBox', 'Станет']}>
                  {serials.toSend.map((x) => (
                    <tr key={x.sw} className={x.overwrites ? styles.rowWarn : ''}>
                      <td className={styles.mono}>{x.sw}</td>
                      <td className={styles.mono}>{x.oldSerial ?? <span className={styles.muted}>пусто</span>}</td>
                      <td className={styles.mono}>{x.newSerial}</td>
                    </tr>
                  ))}
                </Table>
              )}
              {overwrites > 0 && (
                <div className={styles.warning}>
                  Выделенные строки ({overwrites}) перезапишут значения, внесённые в NetBox ранее.
                </div>
              )}
              <Skipped items={[
                ['без серийника в Лире', serials.skipped.noSource],
                ['нет в NetBox', serials.skipped.notInNetbox],
                ['выведены из работы в NetBox', serials.skipped.inactive],
              ]} />
            </>
          )}

          {/* ── Стойки и размещение ── */}
          {plan && !loading && tab === 'racks' && (
            <>
              <p className={styles.intro}>
                Отправляются стойки и размещение коммутаторов. Пассивное оборудование —
                патч-панели, органайзеры — не отправляется: в NetBox стойка содержит
                устройства с моделью и производителем.
              </p>
              {includeBox('racks', newRacks + racks.placements.length + racks.moves.length, plan.siteProblem ? 'площадка не определена' : null)}
              {racks.racks.length > 0 && (
                <Table head={['Стойка', 'Высота', 'В NetBox']}>
                  {racks.racks.map((r) => (
                    <tr key={r.name}>
                      <td>{r.name}</td>
                      <td>{r.height ? `${r.height}U` : <span className={styles.muted}>не указана</span>}</td>
                      <td>{r.existingId ? 'есть' : <b>будет создана</b>}</td>
                    </tr>
                  ))}
                </Table>
              )}
              {racks.placements.length > 0 && (
                <Table head={['Коммутатор', 'Стойка', 'Юнит', 'Было']}>
                  {racks.placements.map((x) => (
                    <tr key={x.sw}>
                      <td className={styles.mono}>{x.sw}</td>
                      <td>{x.rack}</td>
                      <td>{x.position}</td>
                      <td>{x.currentPosition != null ? `юнит ${x.currentPosition}` : <span className={styles.muted}>не размещён</span>}</td>
                    </tr>
                  ))}
                </Table>
              )}
              {racks.moves.length > 0 && (
                <>
                  <Table head={['Перенос', 'Из стойки', 'В стойку', 'Юнит']}>
                    {racks.moves.map((x) => (
                      <tr key={x.sw} className={styles.rowWarn}>
                        <td className={styles.mono}>{x.sw}</td>
                        <td>{x.currentRack}</td>
                        <td>{x.rack}</td>
                        <td>{x.position}</td>
                      </tr>
                    ))}
                  </Table>
                  {/* Перенос в другую стойку — существенное изменение,
                      поэтому включается отдельной отметкой */}
                  {isCoordinator && !openRequest && (
                    <label className={styles.checkbox}>
                      <input type="checkbox" checked={includeMoves} disabled={!selection.racks}
                        onChange={(e) => setIncludeMoves(e.target.checked)} />
                      Включить и переносы между стойками ({racks.moves.length})
                    </label>
                  )}
                </>
              )}
              {!newRacks && !racks.placements.length && !racks.moves.length && (
                <div className={styles.ok}>Размещение в NetBox совпадает с нашим.</div>
              )}
              <Skipped items={[['пропущено', racks.skipped.map((x) => `${x.sw} — ${x.reason}`)]]} />
            </>
          )}

          {/* ── Новые устройства ── */}
          {plan && !loading && tab === 'devices' && (
            <>
              <p className={styles.intro}>
                Коммутаторы, которые есть у нас, но отсутствуют в NetBox. Заводятся на площадке
                объекта с ролью из настроек сервера; тип подбирается по модели только при точном
                совпадении — угадывать по похожему названию нельзя. В комментарии NetBox
                отмечается, что запись заведена из Switch Inspector, и номер запроса.
              </p>
              {includeBox('devices', devices.toCreate.length, plan.siteProblem ? 'площадка не определена' : null)}
              {devices.toCreate.length === 0 ? (
                <div className={styles.ok}>Заводить нечего.</div>
              ) : (
                <Table head={['Коммутатор', 'Тип в NetBox', 'Серийный номер']}>
                  {devices.toCreate.map((x) => (
                    <tr key={x.sw}>
                      <td className={styles.mono}>{x.sw}</td>
                      <td>{x.deviceTypeName}</td>
                      <td className={styles.mono}>{x.serial ?? <span className={styles.muted}>нет</span>}</td>
                    </tr>
                  ))}
                </Table>
              )}
              {devices.skipped.length > 0 && (
                <div className={styles.note}>
                  <b>Нельзя завести автоматически ({devices.skipped.length}):</b>
                  <ul className={styles.list}>
                    {devices.skipped.map((x) => <li key={x.sw}><span className={styles.mono}>{x.sw}</span> — {x.reason}</li>)}
                  </ul>
                </div>
              )}
              {devices.toCreate.length > 0 && (
                <div className={styles.note}>
                  Разместить новые устройства в стойках можно следующим запросом — после того,
                  как они появятся в NetBox.
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.footer}>
          {isCoordinator && plan && !loading && !openRequest && (
            <>
              <textarea
                className={styles.comment}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Комментарий для контролёра — необязательно"
                rows={2}
                maxLength={2000}
                aria-label="Комментарий к запросу"
              />
              <div className={styles.footerRow}>
                <span className={styles.summary}>
                  В запрос: {hasAnyBlock(blocks)
                    ? describeCounts({
                      serials: blocks.serials ? serials.toSend.length : 0,
                      racks: blocks.racks ? newRacks : 0,
                      placements: blocks.racks ? racks.placements.length : 0,
                      moves: blocks.moves ? racks.moves.length : 0,
                      devices: blocks.devices ? devices.toCreate.length : 0,
                    })
                    : 'ничего не выбрано'}
                </span>
                <div className={styles.spacer} />
                <button className={`${styles.button} ${styles.primary}`}
                  disabled={Boolean(sending) || !hasAnyBlock(blocks) || !plan.writeEnabled}
                  title={!plan.writeEnabled ? 'Токен записи NetBox не задан на сервере' : ''}
                  onClick={createRequest}>
                  Отправить на проверку
                </button>
              </div>
            </>
          )}
          <div className={styles.footerRow}>
            {isEditor && !isCoordinator && plan && !loading && !underReview && (
              <button className={`${styles.button} ${ready ? '' : styles.primary}`} disabled={Boolean(sending)} onClick={toggleReady}>
                {ready ? 'Снять отметку «Готово к отправке»' : 'Готово к отправке'}
              </button>
            )}
            {isCoordinator && ready && !openRequest && (
              <span className={styles.summary}>Отмечен готовым: {ready.markedByName || ready.markedBy}</span>
            )}
            {sending && <span className={styles.subtitle}>{sending}</span>}
            <div className={styles.spacer} />
            <button className={styles.button} onClick={load} disabled={loading || Boolean(sending)}>Обновить</button>
            <button className={styles.button} onClick={onClose} disabled={Boolean(sending)}>Закрыть</button>
          </div>
        </div>
      </div>
    </>
  );
}

function countFor(tab, plan) {
  if (tab === 'serials') return plan.serials.toSend.length;
  if (tab === 'racks') {
    return plan.racks.racks.filter((r) => !r.existingId).length
      + plan.racks.placements.length + plan.racks.moves.length;
  }
  return plan.devices.toCreate.length;
}

function Table({ head, children }) {
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead><tr>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Skipped({ items }) {
  const filled = items.filter(([, list]) => list.length);
  if (!filled.length) return null;
  return (
    <div className={styles.note}>
      {filled.map(([label, list]) => (
        <div key={label}>Не отправляется, {label}: {list.length}</div>
      ))}
    </div>
  );
}
